import { Injectable } from "@angular/core";
import { HttpBackend, HttpClient, HttpHeaders } from "@angular/common/http";
import { CookieService } from "ngx-cookie-service";
import { Router } from "@angular/router";
import { GlobalVariables } from "../services/helper.service";
import {
  BehaviorSubject,
  concat,
  forkJoin,
  Observable,
  PartialObserver,
  Subscriber,
} from "rxjs";
import { environment } from "../../environments/environment";
import io from "socket.io-client";
import { ActiveCameraVisit } from "app/models/camerasupervision.model";

export interface VideoPlayback {
  id: string;
  cameraId: string;
  started: boolean;
  completed: boolean;
}
export class VideoServerUser {
  userId!: string;
  companyId!: string;
  organisationId!: string; // company or location ID
  alertServerToken!: string;
  lyraApiToken!: string;

  static fromObject(obj: any): VideoServerUser | null {
    if (typeof obj !== "object") return null;
    if (typeof obj.userId !== "string") return null;
    if (typeof obj.companyId !== "string") return null;
    if (typeof obj.userowerid !== "string") return null;
    if (typeof obj.authToken !== "string") return null;
    if (typeof obj.everonApiToken !== "string") return null;

    return {
      userId: obj.userId,
      companyId: obj.companyId,
      organisationId: obj.userowerid,
      alertServerToken: obj.authToken,
      lyraApiToken: obj.everonApiToken,
    };
  }
}

@Injectable()
export class VideoServerService {
  constructor(
    private cookies: CookieService,
    private router: Router,
    private globals: GlobalVariables,
    backend: HttpBackend
  ) {
    this.http = new HttpClient(backend);
    this.onlineVideoObservable = new Observable((subscriber) => {
      this.onlineVideoSubscriber = subscriber;
    });
  }

  private onlineVideoObservable: Observable<boolean>;
  private onlineVideoSubscriber!: Subscriber<boolean>;

  //
  // Observable for receiving online/offline status of the video server
  //
  get isConnected(): Observable<boolean> {
    return this.onlineVideoObservable;
  }

  private http: HttpClient;
  private baseUrl = environment.videoServerBaseUrl;
  private socket: any = null;
  private users: VideoServerUser[] = [];
  private activeVisitsSubjects: Map<
    string,
    BehaviorSubject<ActiveCameraVisit[]>
  > = new Map<string, BehaviorSubject<ActiveCameraVisit[]>>();
  private _combinedVisits$: BehaviorSubject<ActiveCameraVisit[]> =
    new BehaviorSubject<ActiveCameraVisit[]>([]);

  // Local array to track video playback status.
  // This ensures that start/stop only happens once per camera.
  private videoPlaybackArray: VideoPlayback[] = [];

  login(): Observable<any> {
    const url = `${this.baseUrl}/api/tokens/portal`;
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("session-token"),
      }),
    };
    return this.http.get(url, options);
  }

  getAllCameraVisits(minutes: string): Observable<any> {
    const url = `${this.baseUrl}/api/schedule?f=${minutes}`;
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("videoserver-session-token"),
      }),
    };
    return this.http.get(url, options);
  }

  getActiveCameraVisits(): Observable<any> {
    const url = `${this.baseUrl}/api/visits`;
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("videoserver-session-token"),
      }),
    };
    return this.http.get(url, options);
  }

  getAllCameraGroups(): Observable<any> {
    const url = `${this.baseUrl}/api/cameragroups`;
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("videoserver-session-token"),
      }),
    };
    return this.http.get(url, options);
  }

  saveNewVisit(groupId: string, data: any): Observable<any> {
    const url = `${this.baseUrl}/api/cameragroups/${groupId}/schedule`;
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("videoserver-session-token"),
      }),
    };
    return this.http.post(url, data, options);
  }

  editVisit(groupId: string, scheduleId: string, data: { time: { hour: number; minute: number }; duration: number; gracePeriod: number }): Observable<any> {
    const url = `${this.baseUrl}/api/cameragroups/${groupId}/schedule/${scheduleId}`;
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("videoserver-session-token"),
      }),
    };
    return this.http.put(url, data, options);
  }

  deleteVisit(groupId: string, scheduleId: string): Observable<any> {
    const url = `${this.baseUrl}/api/cameragroups/${groupId}/schedule/${scheduleId}`;
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("videoserver-session-token"),
      }),
    };
    return this.http.delete(url, options);
  }

  deleteVisits(groupId: string, scheduleIds: string[]): Observable<any> {
    const options = {
      headers: new HttpHeaders({
        Authorization: this.cookies.get("videoserver-session-token"),
      }),
    };
    const deleteSchedules$: Observable<any>[] = scheduleIds.map((scheduleId) => {
      const url = `${this.baseUrl}/api/cameragroups/${groupId}/schedule/${scheduleId}`;
      return this.http.delete(url, options);
    });
    return concat(...deleteSchedules$);
  }

  trackVisits(users: VideoServerUser[]) {
    this.users = users;
    if (users.length === 0) return;
    if (this.socket !== null) {
      console.warn(
        "Connecting to video controller websocket while existing websocket exist (closing existing)"
      );
      this.socket.close();
    }
    this.socket = io(`${this.baseUrl}`);

    this.socket.on("connect", () => {
      console.log(`Listening video controller events from ${this.baseUrl}`);
      this.onlineVideoSubscriber?.next(true);
    });
    this.socket.on("disconnect", () => {
      console.log(
        `No connection to video controller events from ${this.baseUrl}`
      );
      this.onlineVideoSubscriber?.next(false);
    });

    for (const user of users) {
      const subject = new BehaviorSubject<ActiveCameraVisit[]>([]);
      this.activeVisitsSubjects.set(user.organisationId, subject);
      this.socket.on("connect", () => {
        console.log(
          `Retrieving visit state after connect: organisation ${user.organisationId}`
        );
        this.updateTrackedVisit(user);
      });
      this.socket.on(`schedule-state-change-${user.organisationId}`, (_data: unknown) => {
        console.log(`Visit state changed: organisation ${user.organisationId}`);
        this.updateTrackedVisit(user);
      });
    }
  }

  untrackVisits() {
    if (this.socket === null) {
      console.warn(
        "Disconnecting from non-existing video server websocket (doing nothing)"
      );
      return;
    }
    this.socket.close();
    this.socket = null;
    this.users = [];
    this.activeVisitsSubjects = new Map<
      string,
      BehaviorSubject<ActiveCameraVisit[]>
    >();
    this._combinedVisits$.next([]);
  }

  getTrackingUsers(): VideoServerUser[] {
    return this.users;
  }

  getTrackedVisitsSubject(
    organisationId: string
  ): BehaviorSubject<ActiveCameraVisit[]> {
    return this.activeVisitsSubjects.get(organisationId)!;
  }

  get combinedVisits$(): Observable<ActiveCameraVisit[]> | null {
    return this._combinedVisits$;
  }

  get isTracking(): boolean {
    return !!this.socket && !!this.socket.id;
  }

  private updateTrackedVisit(user: VideoServerUser) {
    this.getActiveCameraVisitsOfUser(user).subscribe((res) => {
      //Add videoserver token into each visit object
      res = res.map((v) => ({ ...v, userToken: user.alertServerToken }));
      // update this organisation subject
      this.activeVisitsSubjects.get(user.organisationId)?.next(res);

      // ...and also the combined subject
      let combined: ActiveCameraVisit[] = [];
      this.activeVisitsSubjects.forEach((val, key) => {
        combined = combined.concat(val.value);
      });
      this._combinedVisits$.next(combined);
    });
  }

  private getActiveCameraVisitsOfUser(user: VideoServerUser): Observable<any> {
    const url = `${this.baseUrl}/api/visits`;
    const options = {
      headers: new HttpHeaders({
        "x-alertsrv-authtoken": user.alertServerToken,
        "x-alertsrv-sessionid": user.alertServerToken,
        "x-everon-companyid": user.companyId,
      }),
    };
    return this.http.get(url, options);
  }

  startVisit(visit: any): Observable<any> {
    const url = `${this.baseUrl}/api/visits/${visit.id}/started`;
    const options = {
      headers: new HttpHeaders({
        "x-alertsrv-authtoken": visit.token,
        "x-alertsrv-sessionid": visit.token,
        "x-everon-companyid": visit.companyId,
      }),
    };
    return this.http.put(url, null, options);
  }

  cancelStartedVisit(visit: any): Observable<any> {
    const url = `${this.baseUrl}/api/visits/${visit.id}/started`;
    const options = {
      headers: new HttpHeaders({
        "x-alertsrv-authtoken": visit.token,
        "x-alertsrv-sessionid": visit.token,
        "x-everon-companyid": visit.companyId,
      }),
    };
    return this.http.delete(url, options);
  }

  completeVisit(visit: any): Observable<any> {
    const url = `${this.baseUrl}/api/visits/${visit.id}/completed`;
    const options = {
      headers: new HttpHeaders({
        "x-alertsrv-authtoken": visit.token,
        "x-alertsrv-sessionid": visit.token,
        "x-everon-companyid": visit.companyId,
      }),
    };
    return this.http.put(url, null, options);
  }

  snoozeVisit(visit: any): Observable<any> {
    const url = `${this.baseUrl}/api/visits/${visit.id}/snooze`;
    const options = {
      headers: new HttpHeaders({
        "x-alertsrv-authtoken": visit.token,
        "x-alertsrv-sessionid": visit.token,
        "x-everon-companyid": visit.companyId,
      })
    };
    return this.http.put(url, null, options);
  }

  startVisitPlayback(visit: any, cameraId: string): Observable<any>  {
    const options = {
      headers: new HttpHeaders({
        "x-alertsrv-authtoken": visit.token,
        "x-alertsrv-sessionid": visit.token,
        "x-everon-companyid": visit.companyId,
      })
    };
    return this.http
      .put(
        `${this.baseUrl}/api/visits/${visit.id}/stream-started`,
        { cameraId },
        options
      );
  }

  endVisitPlayback(visit: any, cameraId: string): Observable<any>  {
    const options = {
      headers: new HttpHeaders({
        "x-alertsrv-authtoken": visit.token,
        "x-alertsrv-sessionid": visit.token,
        "x-everon-companyid": visit.companyId,
      })
    };
    return this.http
      .put(
        `${this.baseUrl}/api/visits/${visit.id}/stream-ended`,
        { cameraId },
        options
      );
  }

  /**
   * Set video playback started/completed status
   * Update only when started/completed status changes
   * @param visit - visit object 
   * @param cameraId - camera ID, required when starting
   * @param started  - true when starting
   * @param completed - true when completing
   */
  setVideoPlayback(visit: any, cameraId: string | null | undefined, started: boolean, completed: boolean) {
    if (completed) {
      // Complete all cameras for this visit
      // Mark as completed immediately to prevent race conditions
      const itemsToComplete = this.videoPlaybackArray
        .filter(vp => vp.id === visit.id && vp.started && !vp.completed);
      
      itemsToComplete.forEach(vp => {
        vp.completed = true;
        this.endVisitPlayback(visit, vp.cameraId).subscribe({
          next: (result) => {
            // Already marked as completed above
          },
          error: (err) => { 
            console.error(`Failed to end visit playback for visit ${visit.id} and camera ${vp.cameraId}:`, err);
            // Revert completion status on failure
            vp.completed = false;
          }               
        });
      });
    } else if (started && cameraId) {
      // Start a specific camera
      // Check if already exists to prevent duplicates
      const exists = this.videoPlaybackArray.some(vp => vp.id === visit.id && vp.cameraId === cameraId);
      if (!exists) {
        // Add to array immediately to prevent race conditions
        const newPlayback: VideoPlayback = {
          id: visit.id,
          cameraId: cameraId,
          started: true,
          completed: false
        };
        this.videoPlaybackArray.push(newPlayback);
        
        this.startVisitPlayback(visit, cameraId).subscribe({
          next: () => {
            // Already added to array above
          },
          error: (err) => {
            console.error(`Failed to start visit playback for visit ${visit.id} and camera ${cameraId}:`, err);
            // Remove from array on failure
            this.videoPlaybackArray = this.videoPlaybackArray.filter(
              vp => !(vp.id === visit.id && vp.cameraId === cameraId)
            );
          }
        });
      }
    }
  }

  removeFromVideoPlayback(visitId: string): void {
    this.videoPlaybackArray = this.videoPlaybackArray.filter(vp => vp.id !== visitId);
  }
  
  setVideoUrl(rtspUrl: any, dataToken: any, language: string, visitId: string, cameraGroupId: string, deviceId: string): any {
    return `${environment.videoServerBaseUrl}/api/videopage?reportStreamStatus=true&url=${encodeURIComponent(
      rtspUrl
     )}&authtoken=${dataToken}&sessionid=${dataToken}&lang=${language}${visitId ? `&visitid=${visitId}` : ''}${cameraGroupId ? `&cameragroupid=${cameraGroupId}` : ''}${deviceId ? `&cameraid=${deviceId}` : ''}`;
  }

}
